import React, { useEffect, useState } from "react";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import ReportLayout from "../components/ReportLayout";
import InitiativeSpotlight from "../components/InitiativeSpotlight";
import {
    Meta,
    Stat,
    Person,
    StatusPill,
    KpiBlock,
    FlowStep,
    FlowArrow,
} from "../components/ReportPrimitives";

const CLICKUP_EMBED_URL =
    "https://sharing.clickup.com/9012202481/g/h/6-901217762929-7/6b4f5a330fad6b7";

// Edit this when you publish a new version of the Product & IT report.
// Each report has its own LAST_UPDATED constant — they update independently.
const LAST_UPDATED = "May 20, 2026";

/* -------------------- DATA -------------------- */

const PROGRESS_PRODUCT = {
    intro:
        "During the reporting period, MyFora v1, Digital Onboarding v1, and Pay at Table v1 advanced to release stage, and FORA AI Menu Ingestion launched in April — significantly accelerating menu setup. Work continued across POS-related initiatives, FORA Pay, and reporting-related streams. Product work also moved further toward a more unified onboarding and activation model.",
    bullets: [
        "Simplification of onboarding journeys",
        "Stronger linkage between CRM-triggered onboarding and product activation",
        "Clearer user flow across products",
        "Preparation for broader market reuse and standardization",
    ],
    note:
        "A further stream currently in exploration is FORA POS fiscalization for different markets. This is relevant for future multi-market readiness and will require continued assessment from both product and technical perspectives.",
};

const PROGRESS_IT = {
    intro:
        "IT maintained continuity across core services while progressing several enabling capabilities. Maintenance continued across RMS, Digital Menu, Pay at Table, QR, and Admin BO, supporting stability during an active delivery period. Identity and access work advanced through SSO-related development, and integration work continued across key systems.",
    bullets: [
        "Snipe-IT became fully operational in April as the inventory management solution",
        "The ticketing system also became fully operational in April",
        "AWS cost for FORA POS reduced by 50%",
    ],
    foundational: ["Payment Solutions", "AI", "Data Lake / Intelligence"],
    integrations: {
        completed: ["Restimo", "GoPos"],
        inProgress: 7,
        blocked: [
            { name: "VamosPay", reason: "awaiting partner response" },
            { name: "Cornerstone", reason: "awaiting partner response" },
        ],
    },
};

const PAYMENT_LAYERS = [
    {
        n: "01",
        name: "Core Infrastructure & Processing",
        items: ["POS", "SWITCH", "TMS / RKL", "IPG"],
    },
    {
        n: "02",
        name: "Back Office & Management",
        items: [
            "Merchant Management",
            "Billing",
            "CRM",
            "Dashboard",
            "MSG",
        ],
    },
    {
        n: "03",
        name: "Fintech & Security",
        items: ["Wallet", "Token", "TRX Risk", "BIN"],
    },
    {
        n: "04",
        name: "User Experience & Integration",
        items: ["Mobile Application", "Onboarding"],
    },
];

const PAYMENT_SUPPORTS = [
    "payment processing control",
    "merchant onboarding and management",
    "back-office operations",
    "security and transaction risk capabilities",
    "user activation and mobile payment experience",
    "future multi-market expansion",
    "future fiscalization requirements by market",
];

const PRIORITIES = [
    {
        n: "01",
        title: "ISV Viva Transition",
        tag: "May · Critical",
        body:
            "Most urgent delivery priority in May — transition from the Marketplace model to the ISV Viva model. Business-critical change requiring close coordination between Product and IT. Affects onboarding flow, product configuration, payment-related setup, provisioning logic, and dependency sequencing across releases.",
    },
    {
        n: "02",
        title: "Adyen Integration",
        tag: "Quarter · Primary",
        body:
            "Primary integration focus for the next quarter. Expected to be a key enabler of payment standardization, onboarding efficiency, and future scale.",
    },
    {
        n: "03",
        title: "QR Menu Optimization",
        tag: "Mandatory · CCHBC",
        body:
            "Mandatory for CCHBC expansion. Treated as a protected delivery stream alongside Viva and Adyen.",
    },
    {
        n: "04",
        title: "CCHBC Standardization",
        tag: "May 19 · Warsaw Workshop",
        body:
            "Major May milestone — completing the current alignment phase on standardized process and product structure for CCHBC markets, together with the CCHBC team coordinating global FORA product expansion.",
    },
    {
        n: "05",
        title: "ERP Integration Planning",
        tag: "Cross-functional",
        body:
            "Planning to begin in cooperation with Finance, Business, and Operations. Must start early enough to define requirements, ownership, process implications, and sequencing before downstream constraints emerge.",
    },
    {
        n: "06",
        title: "Platform & Market Readiness",
        tag: "Continuous",
        body:
            "Shared identity and SSO rollout, further onboarding automation, reduction of manual handling through exception-based review, selective AI-assisted validation where local rules allow, and continued exploration of FORA POS fiscalization for different markets.",
    },
    {
        n: "07",
        title: "CCH Platform Initiative",
        tag: "Discovery · May – Jun",
        body:
            "Discovery and analysis in cooperation with CCH DTPS and marketing teams — three-layer scalable architecture (BD Operations · HQ Governance · Customer Activation / myFORA). Define rollout timeline. Build kicks off July 2026.",
        crossRef: { href: "#cch-platform", label: "Read the brief" },
    },
    {
        n: "08",
        title: "QR Menu Scalability & Onboarding",
        tag: "Joint with Operations · May – Jul",
        body:
            "Address scalability and onboarding bottlenecks for QR Menu — the entry-level product for almost every venue and the base for downstream rollout (payments, loyalty, analytics, signage). Joint workstream with Operations through May, June, and July.",
        crossRef: { href: "/operations#workstreams", label: "Operations side →" },
    },
];

const KPIS_PRODUCT = [
    {
        n: "01",
        name: "Roadmap Delivery Rate",
        definition:
            "% of committed product initiatives delivered within the planned period.",
        target: "TBD",
        status:
            "Core roadmap items progressed; selected initiatives reached release stage.",
        focus: "Delivery performance across May–July priorities.",
    },
    {
        n: "02",
        name: "Time-to-Market",
        definition:
            "Median time from approved scope to production release.",
        target: "TBD",
        status: "Baseline to be established from recent releases.",
        focus: "Improve release cycle efficiency.",
    },
    {
        n: "03",
        name: "Feature Adoption Rate",
        definition:
            "% of relevant active users using newly launched capabilities.",
        target: "TBD",
        status: "Tracking structure to be finalized.",
        focus: "Measure adoption of new launches.",
    },
    {
        n: "04",
        name: "Release Readiness",
        definition: "% of launches meeting readiness criteria on time.",
        target: "TBD",
        status: "Baseline to be established.",
        focus: "Strengthen launch discipline.",
    },
];

const KPIS_IT = [
    {
        n: "01",
        name: "Platform Uptime",
        definition: "Availability of core production services.",
        target: "TBD",
        status:
            "Stability maintained through maintenance and operational support.",
        focus: "Maintain reliability during active delivery.",
    },
    {
        n: "02",
        name: "P1 / P2 Incidents",
        definition: "Number of critical customer-impacting incidents.",
        target: "TBD",
        status: "Baseline to be confirmed.",
        focus: "Reduce incident frequency.",
    },
    {
        n: "03",
        name: "MTTR",
        definition: "Mean time to restore service after critical incidents.",
        target: "TBD",
        status: "Baseline to be confirmed.",
        focus: "Improve recovery speed.",
    },
    {
        n: "04",
        name: "High-Severity Production Defects",
        definition: "Number of Sev1 / Sev2 defects in production.",
        target: "TBD",
        status: "Baseline to be confirmed.",
        focus: "Reduce production quality issues.",
    },
    {
        n: "05",
        name: "Critical Security / Platform Risks",
        definition: "Number of unresolved critical risks or vulnerabilities.",
        target: "TBD",
        status: "Baseline to be confirmed.",
        focus: "Reduce open critical items.",
    },
];

const IT_ROLES = [
    { role: "Data team role" },
    { role: "Integration Engineer" },
    { role: "Mobile App Developer" },
    { role: "Senior Full-Stack Engineer" },
    { role: "Software Engineer · Maintenance & Ad-hoc" },
    { role: "QA Team Lead" },
];

const PRODUCT_ROLES = [
    {
        role: "Back-office & ERP Coordination",
        note: "Cross-team integration with Finance, Business, and Operations on ERP rollout",
    },
    {
        role: "Hospitality Pillar",
        note: "Digital Menu, Pay at Table, QR Menu, Stand Menu — and especially FORA POS",
    },
];

const TOTAL_NEW_ROLES = IT_ROLES.length + PRODUCT_ROLES.length;

const JOINT_MILESTONES = [
    {
        date: "May 15, 2026",
        title: "ERP Vendor & Implementation Partner",
        detail: "Selection and contracting decision",
        piit: "Robert Rozić",
        business: "TBD",
        operations: "TBD",
        status: "Pending",
    },
    {
        date: "May 19, 2026",
        title: "Warsaw Workshop",
        detail: "CCHBC standardization phase complete",
        piit: "Karlo Duđerija",
        business: "Ognjen Zlački",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "May 20, 2026",
        title: "Adyen Integration Kickoff",
        detail: "Live credentials for onboarding first client",
        piit: "Robert Slišković",
        business: "TBD",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "May 31, 2026",
        title: "Lodging & Taxi Marketplaces",
        detail: "Transfer part of merchants onto new dedicated marketplaces",
        piit: "Robert Rozić",
        business: "Petar Kostić",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "May 31, 2026",
        title: "ERP Requirements Lock",
        detail: "Scope, ownership, and integration model agreed",
        piit: "Robert Rozić",
        business: "TBD",
        operations: "TBD",
        status: "Pending",
    },
    {
        date: "May 31, 2026",
        title: "First 200 Clients · Viva → Adyen",
        detail: "Onboard first 200 clients from Viva marketplace to Adyen",
        piit: "Robert Rozić",
        business: "Petar Kostić",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "Jun 1, 2026",
        title: "HubSpot CRM × Digital Onboarding Integration",
        detail: "Connect CRM-triggered onboarding to provisioning software myFora",
        piit: "Karlo Duđerija",
        business: "Marko Pavičić",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "Jul 31, 2026",
        title: "First CCHBC Market · Unified Stack",
        detail: "Standardized onboarding live in pilot market",
        piit: "Doris Rozic",
        business: "TBD",
        operations: "TBD",
        status: "On Track",
    },
    {
        date: "Continuous",
        title: "FORA Payment Infrastructure",
        detail: "Internal payment platform development & alignment",
        piit: "Karlo Duđerija",
        business: "TBD",
        operations: "TBD",
        status: "On Track",
    },
];

const OPEN_DECISIONS = [
    {
        question: "Single source of truth for merchant data",
        impact:
            "HubSpot CRM (CBO), Digital Onboarding (Product), ERP — which system is master?",
        target: "May 2026",
    },
    {
        question: "HubSpot CRM × Digital Onboarding ownership & governance",
        impact:
            "CBO owns HubSpot for Sales; Product/IT integrates for onboarding — who owns fields, workflows, governance?",
        target: "May 2026",
    },
    {
        question: "CCHBC market expansion sync",
        impact:
            "Standardisation, market sequencing, and product readiness — needs alignment ASAP with CCHBC team.",
        target: "May 2026",
    },
    {
        question: "ERP vendor & implementation partner",
        impact: "Defines May–July sequencing and resourcing",
        target: "May 2026",
    },
];

const SYNC_CADENCE = [
    {
        name: "Product/IT × Operations",
        freq: "TBD",
        purpose: "Onboarding · QR Menu · POS rollout — cadence to define",
    },
    {
        name: "ERP Steering Group",
        freq: "TBD",
        purpose: "Scope · ownership · integration milestones — cadence to define",
    },
    {
        name: "CCHBC Alignment",
        freq: "Weekly",
        purpose: "Standardization · market readiness",
    },
    {
        name: "Finance × Payment Streams",
        freq: "TBD",
        purpose: "Adyen · Viva · ISV transition — cadence to define",
    },
];

const RISK_DEPS = [
    "Adyen",
    "CCHBC market alignment",
    "Finance, Business, and Operations for ERP planning",
    "Identity and SSO rollout",
    "Continued platform stability during active delivery and transition work",
    "VamosPay & Cornerstone — partner-side delays blocking 2 active integrations",
];

/* -------------------- COMPONENT -------------------- */

const NAV_ITEMS = [
    { href: "#summary", label: "Summary" },
    { href: "#progress", label: "Progress" },
    { href: "#priorities", label: "Priorities" },
    { href: "#payment", label: "Payment" },
    { href: "#kpis", label: "KPIs" },
    { href: "#coordination", label: "B&O" },
    { href: "#roadmap", label: "Roadmap" },
    { href: "#risks", label: "Risks" },
];

const Report = () => {
    return (
        <ReportLayout navItems={NAV_ITEMS} lastUpdated={LAST_UPDATED} reportType="product-it">
            {/* HERO */}
            <section
                id="top"
                className="relative bg-black text-white overflow-hidden"
                data-testid="hero-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-90" />
                <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[var(--fora-red-1)] opacity-25 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32">
                    <div className="flex items-center gap-3 rise">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            Progress Report · Confidential
                        </span>
                    </div>

                    <h1
                        className="rise d1 mt-6 font-[800] tracking-[-0.04em] leading-[0.95] text-4xl sm:text-6xl lg:text-[88px]"
                        data-testid="hero-title"
                    >
                        Product&nbsp;and&nbsp;IT{" "}
                        <br />
                        Progress.
                    </h1>

                    <p className="rise d2 mt-6 max-w-2xl text-sm sm:text-lg text-white/80 leading-relaxed">
                        Reporting period{" "}
                        <span className="text-white font-semibold">
                            March – April 2026
                        </span>
                        . Forward view{" "}
                        <span className="text-white font-semibold">
                            May – July 2026
                        </span>
                        . Prepared for the executive team to align on what
                        shipped, what is in motion, and what comes next.
                    </p>

                    <div className="rise d3 mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10 max-w-4xl">
                        <Meta label="Submitted to" value="Ivan Brkić" sub="Managing Director" />
                        <Meta label="Reporting" value="Mar – Apr 2026" sub="Build + Direction" />
                        <Meta label="Forward" value="May – Jul 2026" sub="Delivery quarter" />
                        <Meta label="Owner" value="Franjo Vučić" sub="" />
                    </div>

                    <div className="rise d4 mt-12 flex flex-wrap items-center gap-3">
                        <span className="chip">
                            <span className="red-dot" /> CTO · Robert Rozić
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> CPO · Karlo Duđerija
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Doris Rozić Brajković
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Robert Slišković
                        </span>
                        <span className="chip">
                            <span className="red-dot" /> Franjo Vučić
                        </span>
                    </div>
                </div>

                <div className="fora-strip border-y border-white/10">
                    <div className="fora-strip-track">
                        {Array.from({ length: 2 }).map((_, k) => (
                            <div key={k} className="inline-flex">
                                <span>ISV Viva Transition</span>
                                <span className="pip" />
                                <span>Adyen Integration</span>
                                <span className="pip" />
                                <span>QR Menu Optimization</span>
                                <span className="pip" />
                                <span>CCHBC · May 19 Warsaw</span>
                                <span className="pip" />
                                <span>Onboarding · SSO · Identity</span>
                                <span className="pip" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CROSS-CUTTING INITIATIVE SPOTLIGHT */}
            <InitiativeSpotlight />

            {/* 01 — EXECUTIVE SUMMARY */}
            <section
                id="summary"
                className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28"
                data-testid="summary-section"
            >
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <span className="red-dot" />
                            <span className="section-num">01 — Summary</span>
                        </div>
                        <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                            Executive
                            <br />
                            summary.
                        </h2>
                    </div>
                    <div className="col-span-12 lg:col-span-8" data-testid="summary-body">
                        <p className="text-2xl sm:text-[28px] leading-[1.35] font-[500] tracking-[-0.01em]">
                            During March and April, Product and IT focused on
                            advancing key{" "}
                            <span className="bg-[var(--fora-red-1)] text-white px-2">
                                FORA initiatives
                            </span>{" "}
                            while building the operating foundation for{" "}
                            <em className="not-italic font-[700]">faster onboarding</em>,{" "}
                            <em className="not-italic font-[700]">more consistent user management</em>, and{" "}
                            <em className="not-italic font-[700]">improved scalability across markets</em>.
                        </p>

                        <div className="mt-8 space-y-5 text-sm sm:text-base leading-relaxed text-[var(--fora-ink)]/85 break-words">
                            <p>
                                The strategic direction is to connect{" "}
                                <strong>HubSpot CRM</strong>, <strong>Digital Onboarding</strong>,{" "}
                                <strong>MyFora</strong>, and related FORA services into a more
                                integrated activation flow supported by a shared user model and
                                single sign-on. The objective is to reduce onboarding time,
                                minimize manual intervention, improve provisioning consistency,
                                and lower operational load on Support and Sales.
                            </p>
                            <p>
                                A major execution priority in May is the transition from the{" "}
                                <strong>Marketplace model to the ISV Viva model</strong>. This
                                affects onboarding, provisioning, payment setup, and release
                                sequencing, and remains one of the most important delivery items
                                in the current quarter.
                            </p>
                            <p>
                                In parallel, Product and IT are working on unifying processes
                                and products for all <strong>CCHBC markets</strong>. The
                                current standardization phase is planned to conclude at the{" "}
                                <strong>May 19 workshop in Warsaw</strong>, together with the
                                CCHBC team coordinating global FORA product expansion from
                                their side. This is one of the key priorities in May.
                            </p>
                            <p>
                                On the integration side, <strong>Adyen</strong> is the primary
                                focus for the next quarter. <strong>QR Menu optimization</strong>{" "}
                                is also mandatory for CCHBC expansion and must remain
                                protected as a delivery stream. In addition,{" "}
                                <strong>ERP integration planning</strong> needs to begin in
                                cooperation with Finance, Business, and Operations to address
                                future process, data, and operational dependencies early.
                            </p>
                            <p>
                                Operationally, IT governance improved during the period through
                                the rollout of <strong>Snipe-IT</strong> for inventory
                                management, while the <strong>ticketing system</strong> has been
                                fully operational since April. Infrastructure efficiency also
                                improved materially, with{" "}
                                <strong>AWS cost for FORA POS reduced by 50%</strong>.
                            </p>
                            <p>
                                In addition to external partner-related work, Product
                                and IT are continuing to develop{" "}
                                <strong>FORA's own payment infrastructure</strong> as
                                the internal foundation for processing, merchant
                                operations, onboarding, risk controls, and future
                                multi-market readiness.
                            </p>
                            <p>
                                April delivered tangible product progress:{" "}
                                <strong>Pay at Table v1</strong>,{" "}
                                <strong>FORA AI Menu Ingestion</strong>, and
                                completed integrations with{" "}
                                <strong>Restimo</strong> and{" "}
                                <strong>GoPos</strong>.
                            </p>
                        </div>

                        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
                            <Stat n="6" label="Strategic priorities for May–July" />
                            <Stat n="2" label="KPI families tracked" />
                            <Stat n="50%" label="AWS cost reduction (FORA POS)" />
                            <Stat n="+8" label="Roles required to protect delivery" />
                        </div>
                    </div>
                </div>
            </section>

            <Separator className="bg-[var(--fora-gray)]" />

            {/* 02 — PROGRESS IN MARCH–APRIL */}
            <section
                id="progress"
                className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 print-avoid-break"
                data-testid="progress-section"
            >
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="red-dot" />
                            <span className="section-num">02 — Progress</span>
                        </div>
                        <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                            Progress in
                            <br />
                            March – April.
                        </h2>
                    </div>
                    <p className="max-w-md text-[var(--fora-muted)] text-base">
                        What advanced across Product and IT during the
                        reporting period.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                    <article
                        className="fora-card flex flex-col gap-4"
                        data-testid="progress-product"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                A — Product
                            </span>
                            <ArrowUpRight className="w-5 h-5" />
                        </div>
                        <h3 className="font-[700] text-2xl sm:text-3xl tracking-[-0.02em] leading-tight">
                            Product
                        </h3>
                        <p className="text-[var(--fora-muted)] leading-relaxed">
                            {PROGRESS_PRODUCT.intro}
                        </p>
                        <div className="text-sm font-bold tracking-[0.18em] uppercase mt-2 text-[var(--fora-ink)]">
                            Main progress areas
                        </div>
                        <ul className="space-y-2">
                            {PROGRESS_PRODUCT.bullets.map((b, i) => (
                                <li key={i} className="flex gap-3 text-sm leading-relaxed">
                                    <span className="red-dot mt-[7px] shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-sm leading-relaxed text-[var(--fora-muted)] border-l-2 border-[var(--fora-red-1)] pl-4 mt-2">
                            {PROGRESS_PRODUCT.note}
                        </p>
                    </article>

                    <article
                        className="fora-card flex flex-col gap-4"
                        data-testid="progress-it"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                B — IT
                            </span>
                            <ArrowUpRight className="w-5 h-5" />
                        </div>
                        <h3 className="font-[700] text-2xl sm:text-3xl tracking-[-0.02em] leading-tight">
                            IT
                        </h3>
                        <p className="text-[var(--fora-muted)] leading-relaxed">
                            {PROGRESS_IT.intro}
                        </p>
                        <div className="text-sm font-bold tracking-[0.18em] uppercase mt-2 text-[var(--fora-ink)]">
                            Operational tooling & efficiency
                        </div>
                        <ul className="space-y-2">
                            {PROGRESS_IT.bullets.map((b, i) => (
                                <li key={i} className="flex gap-3 text-sm leading-relaxed">
                                    <span className="red-dot mt-[7px] shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2">
                            <div className="text-sm font-bold tracking-[0.18em] uppercase text-[var(--fora-ink)] mb-2">
                                Foundational work
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {PROGRESS_IT.foundational.map((f) => (
                                    <span
                                        key={f}
                                        className="inline-flex items-center px-3 py-1 border border-[var(--fora-ink)] text-xs font-semibold tracking-[0.14em] uppercase"
                                    >
                                        {f}
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Integrations snapshot */}
                        <div
                            className="mt-3 border-t border-[var(--fora-gray)] pt-3"
                            data-testid="integrations-snapshot"
                        >
                            <div className="text-sm font-bold tracking-[0.18em] uppercase text-[var(--fora-ink)] mb-3">
                                Integrations Snapshot
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                <div className="border border-[var(--fora-ink)] p-2.5">
                                    <div className="font-[800] text-xl text-[var(--fora-red-1)] tracking-[-0.04em] leading-none">
                                        {PROGRESS_IT.integrations.completed.length}
                                    </div>
                                    <div className="mt-1 text-[9px] font-bold tracking-[0.18em] uppercase text-[var(--fora-muted)]">
                                        Completed
                                    </div>
                                    <div className="mt-1 text-[11px] leading-snug text-[var(--fora-ink)]">
                                        {PROGRESS_IT.integrations.completed.join(
                                            " · "
                                        )}
                                    </div>
                                </div>
                                <div className="border border-[var(--fora-ink)] p-2.5">
                                    <div className="font-[800] text-xl text-[var(--fora-ink)] tracking-[-0.04em] leading-none">
                                        {PROGRESS_IT.integrations.inProgress}
                                    </div>
                                    <div className="mt-1 text-[9px] font-bold tracking-[0.18em] uppercase text-[var(--fora-muted)]">
                                        In Progress
                                    </div>
                                    <div className="mt-1 text-[11px] leading-snug text-[var(--fora-muted)]">
                                        active partner integrations
                                    </div>
                                </div>
                                <div className="border border-[var(--fora-red-1)] p-2.5">
                                    <div className="font-[800] text-xl text-[var(--fora-red-1)] tracking-[-0.04em] leading-none">
                                        {PROGRESS_IT.integrations.blocked.length}
                                    </div>
                                    <div className="mt-1 text-[9px] font-bold tracking-[0.18em] uppercase text-[var(--fora-red-1)]">
                                        Blocked
                                    </div>
                                    <div className="mt-1 text-[11px] leading-snug text-[var(--fora-ink)]">
                                        {PROGRESS_IT.integrations.blocked
                                            .map((b) => b.name)
                                            .join(" · ")}
                                    </div>
                                </div>
                            </div>
                            <p className="mt-2 text-[11px] text-[var(--fora-muted)] leading-snug">
                                <strong className="text-[var(--fora-red-1)]">
                                    VamosPay
                                </strong>{" "}
                                and{" "}
                                <strong className="text-[var(--fora-red-1)]">
                                    Cornerstone
                                </strong>{" "}
                                blocked — awaiting partner response.
                            </p>
                        </div>
                    </article>
                </div>
            </section>

            {/* 03 — STRATEGIC DIRECTION */}
            <section
                id="direction"
                className="bg-black text-white relative overflow-hidden print-avoid-break"
                data-testid="direction-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-80" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    03 — Strategic Direction
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                One flow.
                                <br />
                                One identity.
                            </h2>
                        </div>
                        <p className="max-w-md text-white/70 text-base">
                            The shared objective of Product and IT is to reduce
                            onboarding complexity and time-to-go-live by making
                            onboarding more automated, more integrated, and less
                            dependent on manual handling.
                        </p>
                    </div>

                    {/* Flow diagram */}
                    <div
                        className="border border-white/15 p-6 sm:p-8 mb-10"
                        data-testid="flow-diagram"
                    >
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-5">
                            The intended operating flow
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-3 text-base sm:text-lg font-[600] tracking-[-0.01em]">
                            <FlowStep>HubSpot CRM</FlowStep>
                            <FlowArrow />
                            <FlowStep>Digital Onboarding</FlowStep>
                            <FlowArrow />
                            <FlowStep>FORA backend / identity layer</FlowStep>
                            <FlowArrow />
                            <FlowStep>Unified user creation</FlowStep>
                            <FlowArrow />
                            <FlowStep highlight>
                                MyFora &amp; FORA products via shared SSO
                            </FlowStep>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-10">
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/55 mb-3">
                                This direction supports
                            </div>
                            <ul className="space-y-3">
                                {[
                                    "Faster onboarding",
                                    "Fewer manual setup and approval steps",
                                    "More consistent provisioning",
                                    "Lower load on Support and Sales",
                                    "Stronger cross-product access and identity consistency",
                                ].map((b, i) => (
                                    <li key={i} className="flex gap-3 text-base leading-relaxed">
                                        <span className="red-dot mt-[10px] shrink-0" />
                                        <span>{b}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="space-y-6 text-white/80 leading-relaxed">
                            <div>
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-2">
                                    Workstream 1
                                </div>
                                <h3 className="font-[700] text-xl tracking-[-0.01em] mb-2 text-white">
                                    One user base. One SSO.
                                </h3>
                                <p>
                                    Product and IT are progressing toward one
                                    user base and one SSO, reducing fragmentation
                                    across products and improving both customer
                                    experience and internal manageability.
                                </p>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-2">
                                    Workstream 2 · May 19 Warsaw Workshop
                                </div>
                                <h3 className="font-[700] text-xl tracking-[-0.01em] mb-2 text-white">
                                    CCHBC market unification.
                                </h3>
                                <p>
                                    Unifying processes and products for all
                                    CCHBC markets — a more standardized
                                    model for onboarding, setup, activation, and
                                    product structure across countries. Target
                                    is to finalize this standardization phase at
                                    the May 19 workshop in Warsaw, together with
                                    the CCHBC team leading global FORA
                                    product expansion.
                                </p>
                            </div>
                            <p className="text-white/70 border-l-2 border-[var(--fora-red-1)] pl-4">
                                Where country-specific rules allow, the
                                longer-term direction also includes{" "}
                                <span className="text-white font-semibold">
                                    AI-assisted checks
                                </span>{" "}
                                to reduce manual validation and keep manual
                                review limited to exception cases.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 04 — FORA PAYMENT INFRASTRUCTURE */}
            <section
                id="payment"
                className="bg-[var(--fora-gray)] border-y border-[var(--fora-gray)] print-avoid-break"
                data-testid="payment-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    04 — Payment Infrastructure
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Our internal
                                <br />
                                payment platform.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            FORA Payment Infrastructure is{" "}
                            <strong className="text-[var(--fora-ink)]">
                                separate from Viva and Adyen
                            </strong>{" "}
                            — it is the internal payment foundation built and
                            owned by FORA.
                        </p>
                    </div>

                    <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-[var(--fora-ink)]/85 mb-10">
                        In parallel with external payment-related priorities,
                        Product and IT are continuing to develop and align FORA's
                        own payment infrastructure as a core internal capability.
                        It represents the internal foundation required for
                        transaction processing, merchant operations, onboarding,
                        risk control, and future multi-market readiness.
                    </p>

                    {/* Four functional layers */}
                    <div
                        className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
                        data-testid="payment-layers-grid"
                    >
                        {PAYMENT_LAYERS.map((layer) => (
                            <article
                                key={layer.n}
                                className="bg-white border border-[var(--fora-ink)] p-5 flex flex-col gap-3"
                                data-testid={`payment-layer-${layer.n}`}
                            >
                                <div className="flex items-baseline justify-between">
                                    <span className="font-[800] text-2xl text-[var(--fora-red-1)] tracking-[-0.04em] leading-none">
                                        {layer.n}
                                    </span>
                                    <span className="text-[9px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)]">
                                        Layer
                                    </span>
                                </div>
                                <h3 className="font-[700] text-base tracking-[-0.01em] leading-snug">
                                    {layer.name}
                                </h3>
                                <div className="flex flex-wrap gap-1.5 mt-1">
                                    {layer.items.map((item) => (
                                        <span
                                            key={item}
                                            className="inline-flex items-center px-2 py-1 bg-black text-white text-[10px] font-bold tracking-[0.1em] uppercase"
                                            data-testid={`payment-item-${item.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "-")}`}
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>

                    {/* What this enables */}
                    <div className="grid lg:grid-cols-2 gap-8 lg:gap-10">
                        <div>
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                                What this enables
                            </div>
                            <ul className="space-y-2">
                                {PAYMENT_SUPPORTS.map((s, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-base leading-relaxed"
                                        data-testid={`payment-support-${i}`}
                                    >
                                        <span className="red-dot mt-[10px] shrink-0" />
                                        <span className="capitalize">{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="bg-black text-white p-6 sm:p-8 relative overflow-hidden">
                            <div className="absolute inset-0 fora-pattern opacity-70" />
                            <div className="relative">
                                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                                    Technical base for external streams
                                </div>
                                <h3 className="font-[700] text-xl tracking-[-0.02em] leading-tight mb-3 text-white">
                                    The internal layer underneath every external
                                    partner integration.
                                </h3>
                                <p className="text-white/75 leading-relaxed text-sm">
                                    This infrastructure also creates the
                                    technical base required to support external
                                    payment-related initiatives — including the{" "}
                                    <strong className="text-white">
                                        ISV Viva transition
                                    </strong>
                                    ,{" "}
                                    <strong className="text-white">
                                        Adyen integration
                                    </strong>
                                    ,{" "}
                                    <strong className="text-white">
                                        CCHBC expansion
                                    </strong>{" "}
                                    requirements, and future{" "}
                                    <strong className="text-white">
                                        ERP integration
                                    </strong>{" "}
                                    dependencies.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 05 — PRIORITIES MAY–JULY */}
            <section
                id="priorities"
                className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 print-avoid-break"
                data-testid="priorities-section"
            >
                <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                    <div>
                        <div className="flex items-center gap-3">
                            <span className="red-dot" />
                            <span className="section-num">
                                05 — Priorities · May – July
                            </span>
                        </div>
                        <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                            Six streams.
                            <br />
                            One quarter.
                        </h2>
                    </div>
                    <p className="max-w-md text-[var(--fora-muted)] text-base">
                        Mandatory and time-sensitive workstreams that anchor
                        delivery for May, June, and July 2026.
                    </p>
                </div>

                {/* Payment Priorities Clarification — splits internal vs external */}
                <div
                    className="bg-[var(--fora-ink)] text-white border-l-4 border-[var(--fora-red-1)] p-5 sm:p-6 mb-10"
                    data-testid="payment-clarification"
                >
                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-2">
                        Payment Priorities Clarification
                    </div>
                    <p className="text-sm sm:text-base leading-relaxed">
                        The{" "}
                        <strong className="text-white">
                            FORA Payment Infrastructure
                        </strong>{" "}
                        is the{" "}
                        <em className="not-italic font-semibold">
                            internal payment capability layer
                        </em>
                        , while the{" "}
                        <strong className="text-white">ISV Viva transition</strong>{" "}
                        and{" "}
                        <strong className="text-white">Adyen integration</strong>{" "}
                        are{" "}
                        <em className="not-italic font-semibold">
                            external partner and market-enablement streams
                        </em>{" "}
                        that depend on or integrate with this infrastructure.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="priorities-grid">
                    {PRIORITIES.map((p) => (
                        <article
                            key={p.n}
                            className="fora-card flex flex-col gap-4"
                            data-testid={`priority-card-${p.n}`}
                        >
                            <div className="flex items-baseline justify-between">
                                <span className="pillar-num">{p.n}</span>
                                <span className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-red-1)]">
                                    {p.tag}
                                </span>
                            </div>
                            <h3 className="font-[700] text-xl tracking-[-0.02em] leading-tight">
                                {p.title}
                            </h3>
                            <p className="text-[var(--fora-muted)] leading-relaxed text-sm">
                                {p.body}
                            </p>
                        </article>
                    ))}
                </div>
            </section>

            {/* 06 — DELIVERY CAPACITY & RESOURCING */}
            <section
                id="capacity"
                className="bg-[var(--fora-gray)] border-y border-[var(--fora-gray)] print-avoid-break"
                data-testid="capacity-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    06 — Delivery Capacity
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                We need
                                <br />
                                eight more.
                            </h2>
                            <p className="mt-6 text-[var(--fora-muted)] leading-relaxed">
                                Current delivery pressure is high because
                                several mandatory streams are running in
                                parallel: the <strong>Viva transition</strong>,{" "}
                                <strong>Adyen integration</strong>,{" "}
                                <strong>QR Menu optimization</strong>, onboarding
                                transformation, identity unification, and
                                CCHBC market standardization. Each
                                initiative is valid and time-sensitive, but
                                together they create a real execution constraint
                                across both Engineering and Product.
                            </p>
                            <p className="mt-4 text-[var(--fora-muted)] leading-relaxed">
                                To deliver these priorities without slippage or
                                quality risk, Product and IT need protected
                                capacity across both{" "}
                                <strong>Engineering</strong> and{" "}
                                <strong>Product</strong> — anchored on three
                                critical streams (<strong>Adyen</strong>,{" "}
                                <strong>Viva transition</strong>,{" "}
                                <strong>QR Menu optimization</strong>) and
                                dedicated product ownership for the{" "}
                                <strong>Hospitality</strong>,{" "}
                                <strong>Retail</strong>, and{" "}
                                <strong>Back-office / ERP</strong> tracks. With
                                the current team structure, this is difficult to
                                sustain alongside maintenance, ongoing releases,
                                and enabling platform work.
                            </p>
                        </div>

                        <div className="col-span-12 lg:col-span-7">
                            <div className="bg-black text-white p-8 sm:p-10 relative overflow-hidden">
                                <div className="absolute inset-0 fora-pattern opacity-80" />
                                <div className="relative">
                                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/60">
                                        Minimum staffing gap
                                    </div>
                                    <div className="mt-3 flex items-baseline gap-4">
                                        <span className="font-[800] text-7xl sm:text-8xl tracking-[-0.05em] leading-none text-[var(--fora-red-1)]">
                                            +{TOTAL_NEW_ROLES}
                                        </span>
                                        <span className="text-white/70 text-lg">
                                            roles
                                        </span>
                                    </div>

                                    <div className="mt-6 grid sm:grid-cols-2 gap-6">
                                        <div data-testid="it-roles-block">
                                            <div className="flex items-baseline justify-between border-b border-white/15 pb-2 mb-3">
                                                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                                    IT — {IT_ROLES.length} roles
                                                </span>
                                            </div>
                                            <ul className="space-y-2">
                                                {IT_ROLES.map((r, i) => (
                                                    <li
                                                        key={i}
                                                        className="flex gap-3 text-sm leading-snug"
                                                        data-testid={`it-role-${i}`}
                                                    >
                                                        <span className="text-[var(--fora-red-1)] font-[800] tracking-[-0.02em] shrink-0">
                                                            +1
                                                        </span>
                                                        <span className="text-white/90">
                                                            {r.role}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div data-testid="product-roles-block">
                                            <div className="flex items-baseline justify-between border-b border-white/15 pb-2 mb-3">
                                                <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                                    Product — {PRODUCT_ROLES.length} roles
                                                </span>
                                            </div>
                                            <ul className="space-y-2">
                                                {PRODUCT_ROLES.map((r, i) => (
                                                    <li
                                                        key={i}
                                                        className="flex gap-3 text-sm leading-snug"
                                                        data-testid={`product-role-${i}`}
                                                    >
                                                        <span className="text-[var(--fora-red-1)] font-[800] tracking-[-0.02em] shrink-0">
                                                            +1
                                                        </span>
                                                        <div className="min-w-0">
                                                            <div className="text-white/90">
                                                                {r.role}
                                                            </div>
                                                            {r.note && (
                                                                <div className="text-[11px] text-white/50 mt-0.5 leading-snug">
                                                                    {r.note}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <p className="mt-6 text-white/70 text-sm leading-relaxed">
                                        Minimum reinforcement to maintain
                                        execution quality across confirmed
                                        priorities while continuing strategic
                                        platform work. Without this increase,
                                        the main risk is reduced focus, slower
                                        delivery, and greater strain on the
                                        teams supporting both roadmap execution
                                        and market expansion.
                                    </p>
                                    <p className="mt-3 text-white/55 text-sm leading-relaxed border-l-2 border-[var(--fora-red-1)] pl-4">
                                        ERP planning will also require active
                                        contribution from Finance, Business, and
                                        Operations — particularly for
                                        requirements, ownership, and process
                                        alignment.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 07 — COORDINATION WITH BUSINESS & OPERATIONS */}
            <section
                id="coordination"
                className="bg-white border-y border-[var(--fora-gray)] print-avoid-break"
                data-testid="coordination-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    07 — B&amp;O Coordination
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Coordinating with
                                <br />
                                Business &amp; Operations.
                            </h2>
                        </div>
                        <p className="max-w-md text-[var(--fora-muted)] text-base">
                            Many of the priorities for May–July depend on close
                            alignment with Business and Operations. This section
                            makes the joint commitments, asks, and decisions
                            explicit.
                        </p>
                    </div>

                    {/* Joint Milestones Table */}
                    <div className="mb-10">
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                            A — Joint Milestones
                        </div>
                        <div
                            className="border border-[var(--fora-ink)]"
                            data-testid="joint-milestones-table"
                        >
                            <div className="hidden md:grid grid-cols-12 px-5 py-3 bg-[var(--fora-gray)] text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-muted)] border-b border-[var(--fora-ink)]">
                                <div className="col-span-2">Date</div>
                                <div className="col-span-3">Milestone</div>
                                <div className="col-span-2">Product / IT</div>
                                <div className="col-span-2">Business</div>
                                <div className="col-span-2">Operations</div>
                                <div className="col-span-1">Status</div>
                            </div>
                            {JOINT_MILESTONES.map((m, i) => (
                                <div
                                    key={i}
                                    className={`grid grid-cols-1 md:grid-cols-12 px-5 py-4 gap-2 md:gap-3 ${
                                        i !== JOINT_MILESTONES.length - 1
                                            ? "border-b border-[var(--fora-gray)]"
                                            : ""
                                    } hover:bg-[var(--fora-gray)]/50 transition-colors`}
                                    data-testid={`milestone-${i}`}
                                >
                                    <div className="md:col-span-2 text-sm font-[700] text-[var(--fora-red-1)]">
                                        {m.date}
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="text-sm font-[700] tracking-[-0.01em] leading-snug">
                                            {m.title}
                                        </div>
                                        <div className="text-xs text-[var(--fora-muted)] mt-0.5 leading-snug">
                                            {m.detail}
                                        </div>
                                    </div>
                                    <div className="md:col-span-2 text-sm text-[var(--fora-ink)]/80">
                                        <span className="md:hidden text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-muted)] block mb-0.5">
                                            Product / IT
                                        </span>
                                        {m.piit}
                                    </div>
                                    <div className="md:col-span-2 text-sm text-[var(--fora-ink)]/80">
                                        <span className="md:hidden text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-muted)] block mb-0.5">
                                            Business
                                        </span>
                                        {m.business}
                                    </div>
                                    <div className="md:col-span-2 text-sm text-[var(--fora-ink)]/80">
                                        <span className="md:hidden text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-muted)] block mb-0.5">
                                            Operations
                                        </span>
                                        {m.operations}
                                    </div>
                                    <div className="md:col-span-1">
                                        <StatusPill status={m.status} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Open Decisions */}
                    <div className="mb-10" data-testid="open-decisions-block">
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                            B — Open Decisions
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {OPEN_DECISIONS.map((d, i) => (
                                <div
                                    key={i}
                                    className="bg-[var(--fora-gray)] p-4"
                                    data-testid={`decision-${i}`}
                                >
                                    <div className="text-sm font-[700] leading-snug">
                                        {d.question}
                                    </div>
                                    <div className="mt-1 text-[11px] text-[var(--fora-muted)] leading-snug">
                                        {d.impact}
                                    </div>
                                    <div className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--fora-red-1)]">
                                        Target · {d.target}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Sync Cadence */}
                    <div data-testid="cadence-block">
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                            C — Sync Cadence
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {SYNC_CADENCE.map((c, i) => (
                                <div
                                    key={i}
                                    className="border border-[var(--fora-ink)] p-4"
                                    data-testid={`cadence-${i}`}
                                >
                                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                                        {c.freq}
                                    </div>
                                    <div className="mt-1.5 text-sm font-[700] tracking-[-0.01em] leading-snug">
                                        {c.name}
                                    </div>
                                    <div className="mt-1 text-[11px] text-[var(--fora-muted)] leading-snug">
                                        {c.purpose}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* 08 — KPI DASHBOARD */}
            <section
                id="kpis"
                className="bg-black text-white relative overflow-hidden print-avoid-break"
                data-testid="kpis-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-70" />
                <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--fora-red-2)] opacity-20 blur-3xl" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    08 — KPI Dashboard
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                How we are
                                <br />
                                measuring it.
                            </h2>
                        </div>
                        <p className="max-w-md text-white/70 text-base">
                            Two KPI families — Product and IT. Targets to be
                            defined in May.
                        </p>
                    </div>

                    <KpiBlock
                        eyebrow="A — Product"
                        title="Product KPIs"
                        subtitle="Delivery · speed · adoption · launch quality."
                        rows={KPIS_PRODUCT}
                        testIdPrefix="kpi-product"
                    />

                    <div className="mt-16">
                        <KpiBlock
                            eyebrow="B — IT"
                            title="IT KPIs"
                            subtitle="Reliability · incidents · recovery · quality · risk."
                            rows={KPIS_IT}
                            testIdPrefix="kpi-it"
                        />
                    </div>

                    <p className="text-xs text-white/45 mt-8 max-w-2xl">
                        Targets marked{" "}
                        <span className="text-[var(--fora-red-1)] font-semibold">
                            TBD
                        </span>{" "}
                        will be locked in once baselines from the current
                        period are finalised.
                    </p>
                </div>
            </section>

            {/* 09 — ROADMAP */}
            <section
                id="roadmap"
                className="bg-[var(--fora-gray)] border-y border-[var(--fora-gray)]"
                data-testid="roadmap-section"
            >
                <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-end justify-between flex-wrap gap-6 mb-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num">
                                    09 — Gantt Roadmap
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Roadmap, live
                                <br />
                                from ClickUp.
                            </h2>
                        </div>
                        <a
                            href={CLICKUP_EMBED_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold tracking-wide uppercase fx-link"
                            data-testid="clickup-open-link"
                        >
                            Open in ClickUp <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>

                    <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-[var(--fora-ink)]/80 mb-8">
                        The embedded Gantt is{" "}
                        <strong className="text-[var(--fora-ink)]">
                            organised per product
                        </strong>{" "}
                        — FORA POS, MyFora, Pay at Table, Digital Onboarding,
                        FORA AI, Digital Menu, QR Menu, Admin BO — with concrete
                        delivery dates for every initiative. Use the timeline
                        below as the single source of truth for{" "}
                        <em className="not-italic font-semibold">
                            what ships when
                        </em>
                        .
                    </p>

                    <div
                        className="bg-white border border-[var(--fora-ink)] no-print"
                        data-testid="clickup-embed-wrapper"
                    >
                        <iframe
                            className="clickup-embed w-full"
                            src={CLICKUP_EMBED_URL}
                            title="FORA Product & IT Roadmap — ClickUp Gantt"
                            style={{
                                background: "transparent",
                                border: 0,
                                minHeight: 700,
                            }}
                            width="100%"
                            height="700"
                            data-testid="clickup-iframe"
                        />
                    </div>

                    {/* Print-only fallback for the ClickUp Gantt */}
                    <div
                        className="iframe-print-fallback bg-white border border-[var(--fora-ink)] p-8 hidden"
                        style={{ display: "none" }}
                        data-testid="clickup-print-fallback"
                    >
                        <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)]">
                            Live Gantt roadmap · ClickUp
                        </div>
                        <p className="font-[700] text-xl mt-2 tracking-[-0.01em]">
                            The interactive Gantt is hosted in ClickUp and not
                            embedded in the printed copy.
                        </p>
                        <p className="text-sm text-[var(--fora-muted)] mt-3 break-all">
                            {CLICKUP_EMBED_URL}
                        </p>
                    </div>

                    <p className="text-xs text-[var(--fora-muted)] mt-4">
                        The embedded view reflects the current state of the
                        Gantt board in ClickUp. Permissions are managed inside
                        ClickUp.
                    </p>
                </div>
            </section>

            {/* 10 — KEY RISKS & DEPENDENCIES */}
            <section
                id="risks"
                className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28 print-avoid-break"
                data-testid="risks-section"
            >
                <div className="grid grid-cols-12 gap-6 md:gap-10">
                    <div className="col-span-12 lg:col-span-4">
                        <div className="flex items-center gap-3">
                            <span className="red-dot" />
                            <span className="section-num">10 — Risks</span>
                        </div>
                        <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                            Key risks &amp;
                            <br />
                            dependencies.
                        </h2>
                    </div>
                    <div className="col-span-12 lg:col-span-8 space-y-6 text-base sm:text-lg leading-relaxed text-[var(--fora-ink)]/85">
                        <p>
                            The main delivery risk is the concentration of
                            multiple business-critical initiatives in the same
                            execution window. The combination of{" "}
                            <strong>Viva</strong>, <strong>Adyen</strong>,{" "}
                            <strong>QR Menu optimization</strong>, onboarding
                            improvements, and CCHBC market standardization
                            creates a high dependency on disciplined sequencing
                            and sufficient engineering capacity.
                        </p>

                        <div>
                            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[var(--fora-red-1)] mb-3">
                                Most important dependencies
                            </div>
                            <ul className="space-y-2">
                                {RISK_DEPS.map((d, i) => (
                                    <li
                                        key={i}
                                        className="flex gap-3 text-base leading-relaxed"
                                        data-testid={`risk-dep-${i}`}
                                    >
                                        <span className="red-dot mt-[10px] shrink-0" />
                                        <span>{d}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <p className="text-[var(--fora-muted)] border-l-2 border-[var(--fora-red-1)] pl-4">
                            Additional execution constraints may arise from
                            country-specific requirements, particularly in areas
                            such as AI-assisted validation and future
                            fiscalization work.
                        </p>
                    </div>
                </div>
            </section>

            {/* 11 — CLOSING VIEW */}
            <section
                id="closing"
                className="bg-black text-white relative overflow-hidden print-avoid-break"
                data-testid="closing-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-70" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
                    <div className="flex items-center gap-3">
                        <span className="red-dot" />
                        <span className="section-num text-white/80">
                            11 — Closing View
                        </span>
                    </div>
                    <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1.05] text-2xl sm:text-3xl lg:text-4xl lg:text-5xl max-w-4xl">
                        Product and IT are balancing active roadmap delivery
                        with the structural work required to{" "}
                        <span className="bg-[var(--fora-red-1)] text-white px-2">
                            improve onboarding speed
                        </span>
                        ,{" "}
                        <span className="bg-[var(--fora-red-1)] text-white px-2">
                            simplify the product ecosystem
                        </span>
                        , and support near-term business priorities.
                    </h2>
                    <div className="mt-8 max-w-3xl space-y-5 text-white/80 text-sm sm:text-base leading-relaxed break-words">
                        <p>
                            March and April strengthened the base through
                            progress in Digital Onboarding, MyFora, identity
                            development, operational tooling, infrastructure
                            cost efficiency, and platform enablement. April
                            also delivered{" "}
                            <strong className="text-white">Pay at Table v1</strong>{" "}
                            and{" "}
                            <strong className="text-white">FORA AI Menu Ingestion</strong>{" "}
                            — two visible wins ahead of the May execution window.
                        </p>
                        <p>
                            The most important focus areas now are the{" "}
                            <strong className="text-white">Viva transition in May</strong>, the{" "}
                            <strong className="text-white">May 19 Warsaw standardization milestone</strong> with CCHBC,{" "}
                            <strong className="text-white">Adyen integration</strong>,{" "}
                            <strong className="text-white">QR Menu optimization</strong>, early{" "}
                            <strong className="text-white">ERP planning</strong> with Finance, Business, and Operations,
                            and continued preparation for broader multi-market
                            readiness.
                        </p>
                        <p className="text-white/55 text-sm pt-2 border-t border-white/10 mt-4">
                            Live Gantt roadmap (ClickUp):{" "}
                            <a
                                href={CLICKUP_EMBED_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-white font-semibold break-all"
                            >
                                {CLICKUP_EMBED_URL}
                            </a>
                        </p>
                    </div>
                </div>
            </section>

            {/* 12 — SIGNOFF */}
            <section
                className="bg-black text-white relative overflow-hidden border-t border-white/10"
                data-testid="signoff-section"
            >
                <div className="absolute inset-0 fora-pattern opacity-80" />
                <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
                    <div className="grid grid-cols-12 gap-6 md:gap-10">
                        <div className="col-span-12 lg:col-span-5">
                            <div className="flex items-center gap-3">
                                <span className="red-dot" />
                                <span className="section-num text-white/80">
                                    12 — Sign-off
                                </span>
                            </div>
                            <h2 className="mt-5 font-[800] tracking-[-0.03em] leading-[1] text-4xl sm:text-5xl">
                                Prepared by the
                                <br />
                                Product &amp; IT team.
                            </h2>
                            <p className="mt-6 text-white/70 max-w-md">
                                For questions, deep-dives, or to request a
                                walkthrough of the roadmap, reach out to the
                                contributors below.
                            </p>
                        </div>

                        <div className="col-span-12 lg:col-span-7 grid sm:grid-cols-2 gap-4">
                            <Person
                                name="Robert Rozić"
                                role="CTO"
                                img="https://customer-assets.emergentagent.com/job_fora-progress-report/artifacts/jp2s5xw6_image.png"
                                email="robert.rozic@master-fb.com"
                            />
                            <Person
                                name="Karlo Duđerija"
                                role="CPO"
                                img="https://customer-assets.emergentagent.com/job_fora-progress-report/artifacts/l03c8p1a_image.png"
                                email="karlo.duderija@master-fb.com"
                            />
                            <Person
                                name="Doris Rozić Brajković"
                                role="myFORA Product Owner"
                                img="https://customer-assets.emergentagent.com/job_fora-progress-report/artifacts/kpqvluvf_T09CKTY2P5K-U0ALRTW6RAN-c4b31865730c-512.png"
                                email="doris.rozic@master-fb.com"
                            />
                            <Person
                                name="Robert Slišković"
                                role="Technical Integration Manager"
                                img="https://customer-assets.emergentagent.com/job_fora-progress-report/artifacts/518r6o06_T09CKTY2P5K-U0ALS585A02-d21843810af2-512.jpg"
                                email="robert.sliskovic@master-fb.com"
                            />
                            <Person
                                name="Franjo Vučić"
                                role="Product & IT"
                                img="https://customer-assets.emergentagent.com/job_fora-progress-report/artifacts/1i8snb7j_image.png"
                                email="franjo.vucic@master-fb.com"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </ReportLayout>
    );
};

export default Report;
